import { useState, useEffect } from 'react';
import {
  X,
  Bell,
  BellOff,
  Clock,
  AlertTriangle,
  TrendingDown,
  Calendar,
  MessageSquare,
  Users,
  Wrench,
  DollarSign,
  CheckCircle,
  Info,
  Volume2,
  Vibrate,
  Mail,
  Smartphone,
  Ticket,
  ArrowLeft,
  MapPin,
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../utils/firebase/client';
import { motion, AnimatePresence } from 'framer-motion';

interface NotificationSettingsProps {
  onClose: () => void;
  onSave: (settings: any) => void;
  userId: string;
  isDarkMode?: boolean;
}

export default function NotificationSettings({
  onClose,
  onSave,
  userId,
  isDarkMode = false,
}: NotificationSettingsProps) {
  const [loading, setLoading] = useState(true);
  const [masterEnabled, setMasterEnabled] = useState(true);

  const [notificationTypes, setNotificationTypes] = useState({
    overdueWells: { enabled: true, sound: true, vibrate: true, email: true, sms: false },
    criticalAlerts: { enabled: true, sound: true, vibrate: true, email: true, sms: true },
    lowProduction: { enabled: true, sound: false, vibrate: true, email: true, sms: false },
    maintenance: { enabled: true, sound: false, vibrate: false, email: true, sms: false },
    scheduleReminders: { enabled: true, sound: true, vibrate: true, email: false, sms: false },
    teamMessages: { enabled: true, sound: true, vibrate: true, email: false, sms: false },
    shiftHandoff: { enabled: true, sound: true, vibrate: false, email: true, sms: false },
    tickets: { enabled: true, sound: false, vibrate: true, email: true, sms: false },
    invoices: { enabled: false, sound: false, vibrate: false, email: true, sms: false },
    systemUpdates: { enabled: false, sound: false, vibrate: false, email: false, sms: false },
    missedDataEntry: { enabled: true, sound: true, vibrate: true, email: false, sms: false },
  });

  const [quietHours, setQuietHours] = useState({
    enabled: false,
    startTime: '22:00',
    endTime: '07:00',
    allowCritical: true,
  });

  // Load settings from Firestore on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Try tenant-scoped collection first, then fall back to legacy /users
        let userDoc;
        if (user?.companyId && user.companyId !== 'default') {
          userDoc = await getDoc(doc(db, 'companies', user.companyId, 'users', userId));
        }
        if (!userDoc?.exists()) {
          userDoc = await getDoc(doc(db, 'users', userId));
        }
        if (userDoc?.exists()) {
          const userData = userDoc.data();
          const prefs = userData.preferences?.notificationSettings;

          if (prefs) {
            setMasterEnabled(prefs.all_notifications_enabled ?? true);

            // Map Firestore prefs to component state
            if (prefs.overdueWells) setNotificationTypes(prev => ({ ...prev, overdueWells: prefs.overdueWells }));
            if (prefs.criticalAlerts) setNotificationTypes(prev => ({ ...prev, criticalAlerts: prefs.criticalAlerts }));
            if (prefs.lowProduction) setNotificationTypes(prev => ({ ...prev, lowProduction: prefs.lowProduction }));
            if (prefs.maintenance) setNotificationTypes(prev => ({ ...prev, maintenance: prefs.maintenance }));
            if (prefs.scheduleReminders) setNotificationTypes(prev => ({ ...prev, scheduleReminders: prefs.scheduleReminders }));
            if (prefs.teamMessages) setNotificationTypes(prev => ({ ...prev, teamMessages: prefs.teamMessages }));
            if (prefs.shiftHandoff) setNotificationTypes(prev => ({ ...prev, shiftHandoff: prefs.shiftHandoff }));
            if (prefs.tickets) setNotificationTypes(prev => ({ ...prev, tickets: prefs.tickets }));
            if (prefs.invoices) setNotificationTypes(prev => ({ ...prev, invoices: prefs.invoices }));
            if (prefs.systemUpdates) setNotificationTypes(prev => ({ ...prev, systemUpdates: prefs.systemUpdates }));
            if (prefs.missedDataEntry) setNotificationTypes(prev => ({ ...prev, missedDataEntry: prefs.missedDataEntry }));

            if (prefs.quiet_hours) {
              setQuietHours({
                enabled: prefs.quiet_hours.enabled ?? false,
                startTime: prefs.quiet_hours.start_time ?? '22:00',
                endTime: prefs.quiet_hours.end_time ?? '07:00',
                allowCritical: prefs.quiet_hours.allow_critical ?? true,
              });
            }
          }
        }
      } catch (error) {
        console.error('Failed to load notification settings:', error);
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [userId]);

  const handleToggleNotificationType = (type: keyof typeof notificationTypes, field: keyof typeof notificationTypes.overdueWells) => {
    setNotificationTypes(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: !prev[type][field],
      },
    }));
  };

  const handleSave = async () => {
    try {
      const settings = {
        all_notifications_enabled: masterEnabled,
        overdueWells: notificationTypes.overdueWells,
        criticalAlerts: notificationTypes.criticalAlerts,
        lowProduction: notificationTypes.lowProduction,
        maintenance: notificationTypes.maintenance,
        scheduleReminders: notificationTypes.scheduleReminders,
        teamMessages: notificationTypes.teamMessages,
        shiftHandoff: notificationTypes.shiftHandoff,
        tickets: notificationTypes.tickets,
        invoices: notificationTypes.invoices,
        systemUpdates: notificationTypes.systemUpdates,
        missedDataEntry: notificationTypes.missedDataEntry,
        quiet_hours: {
          enabled: quietHours.enabled,
          start_time: quietHours.startTime,
          end_time: quietHours.endTime,
          allow_critical: quietHours.allowCritical,
        },
      };

      // Try tenant-scoped collection first, then fall back to legacy /users
      let userRef;
      if (user?.companyId && user.companyId !== 'default') {
        const tenantRef = doc(db, 'companies', user.companyId, 'users', userId);
        const tenantDoc = await getDoc(tenantRef);
        if (tenantDoc.exists()) {
          userRef = tenantRef;
        }
      }
      if (!userRef) {
        userRef = doc(db, 'users', userId);
      }
      
      await updateDoc(userRef, {
        'preferences.notificationSettings': settings,
      });

      onSave(settings);
      toast.success('Notification settings saved');
      onClose();
    } catch (error) {
      console.error('Failed to save notification settings:', error);
      toast.error('Failed to save settings');
    }
  };

  const notificationCategories = [
    {
      id: 'overdueWells' as const,
      icon: Clock,
      color: 'text-orange-600',
      bg: isDarkMode ? 'bg-orange-500/10' : 'bg-orange-50',
      title: 'Overdue Well Checks',
      description: 'Wells that need inspection',
      priority: 'High Priority',
      priorityColor: isDarkMode ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-800',
    },
    {
      id: 'criticalAlerts' as const,
      icon: AlertTriangle,
      color: 'text-red-600',
      bg: isDarkMode ? 'bg-red-500/10' : 'bg-red-50',
      title: 'Critical Alerts',
      description: 'Emergency issues and failures',
      priority: 'Critical',
      priorityColor: isDarkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-800',
    },
    {
      id: 'lowProduction' as const,
      icon: TrendingDown,
      color: 'text-yellow-600',
      bg: isDarkMode ? 'bg-yellow-500/10' : 'bg-yellow-50',
      title: 'Low Production',
      description: 'Wells with declining output',
      priority: 'Medium Priority',
      priorityColor: isDarkMode ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-800',
    },
    {
      id: 'maintenance' as const,
      icon: Wrench,
      color: 'text-blue-600',
      bg: isDarkMode ? 'bg-blue-500/10' : 'bg-blue-50',
      title: 'Maintenance Reminders',
      description: 'Scheduled maintenance tasks',
      priority: 'Normal',
      priorityColor: isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-800',
    },
    {
      id: 'tickets' as const,
      icon: Ticket,
      color: 'text-cyan-600',
      bg: isDarkMode ? 'bg-cyan-500/10' : 'bg-cyan-50',
      title: 'Service Tickets',
      description: 'Ticket assignments and updates',
      priority: 'Normal',
      priorityColor: isDarkMode ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-800',
    },
    {
      id: 'scheduleReminders' as const,
      icon: Calendar,
      color: 'text-purple-600',
      bg: isDarkMode ? 'bg-purple-500/10' : 'bg-purple-50',
      title: 'Schedule Reminders',
      description: 'Upcoming inspections and tasks',
      priority: 'Normal',
      priorityColor: isDarkMode ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-800',
    },
    {
      id: 'teamMessages' as const,
      icon: MessageSquare,
      color: 'text-green-600',
      bg: isDarkMode ? 'bg-green-500/10' : 'bg-green-50',
      title: 'Team Messages',
      description: 'Messages from team members',
      priority: 'Normal',
      priorityColor: isDarkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-800',
    },
    {
      id: 'shiftHandoff' as const,
      icon: Users,
      color: 'text-indigo-600',
      bg: isDarkMode ? 'bg-indigo-500/10' : 'bg-indigo-50',
      title: 'Shift Handoff',
      description: 'Shift change notifications',
      priority: 'Normal',
      priorityColor: isDarkMode ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-800',
    },
    {
      id: 'invoices' as const,
      icon: DollarSign,
      color: 'text-teal-600',
      bg: isDarkMode ? 'bg-teal-500/10' : 'bg-teal-50',
      title: 'Invoices & Payments',
      description: 'Financial notifications',
      priority: 'Low Priority',
      priorityColor: isDarkMode ? 'bg-teal-500/20 text-teal-400' : 'bg-teal-100 text-teal-800',
    },
    {
      id: 'missedDataEntry' as const,
      icon: MapPin,
      color: 'text-amber-600',
      bg: isDarkMode ? 'bg-amber-500/10' : 'bg-amber-50',
      title: 'Missed Data Entry',
      description: 'Remind when leaving well without entry',
      priority: 'High Priority',
      priorityColor: isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-800',
    },
    {
      id: 'systemUpdates' as const,
      icon: Info,
      color: 'text-gray-600',
      bg: isDarkMode ? 'bg-gray-500/10' : 'bg-gray-50',
      title: 'System Updates',
      description: 'App updates and announcements',
      priority: 'Low Priority',
      priorityColor: isDarkMode ? 'bg-gray-500/20 text-gray-400' : 'bg-gray-100 text-gray-800',
    },
  ];

  if (loading) {
    return (
      <div className={`fixed inset-0 z-50 flex items-center justify-center ${isDarkMode ? 'bg-slate-950' : 'bg-white'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed inset-0 z-50 flex flex-col ${isDarkMode ? 'bg-slate-950 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <div className={`
        relative px-4 py-4 shrink-0 flex items-center justify-between
        ${isDarkMode
          ? 'bg-slate-900 border-b border-slate-800'
          : 'bg-gradient-to-r from-purple-600 to-purple-700 shadow-sm text-white'
        }
      `}>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className={isDarkMode ? 'text-white hover:bg-slate-800' : 'text-white hover:bg-white/20'}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-white'}`}>Notification Settings</h2>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-purple-100'}`}>
              Manage alerts and preferences
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className={`p-2 rounded-full transition-colors ${isDarkMode ? 'hover:bg-slate-800 text-gray-400' : 'hover:bg-white/20 text-white'}`}
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">

        {/* Master Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`
            p-4 rounded-xl shadow-sm border
            ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-purple-100'}
          `}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${masterEnabled
                ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
                : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                }`}>
                {masterEnabled ? (
                  <Bell className="h-5 w-5" />
                ) : (
                  <BellOff className="h-5 w-5" />
                )}
              </div>
              <div>
                <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>All Notifications</p>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {masterEnabled ? 'Notifications are currently active' : 'Notifications are paused'}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setMasterEnabled(!masterEnabled);
                toast.success(masterEnabled ? 'All notifications disabled' : 'All notifications enabled');
              }}
              className={`relative w-14 h-8 rounded-full transition-colors ${masterEnabled ? 'bg-purple-600' : 'bg-gray-300 dark:bg-gray-700'}`}
            >
              <div
                className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow-md transition-transform ${masterEnabled ? 'translate-x-6' : 'translate-x-0'
                  }`}
              />
            </button>
          </div>
        </motion.div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3">
          <Card className={`${isDarkMode ? 'bg-green-900/10 border-green-900/20' : 'bg-gradient-to-br from-green-50 to-green-100 border-green-200'} border shadow-sm`}>
            <CardContent className="p-3 text-center">
              <CheckCircle className="h-6 w-6 text-green-600 mb-1 mx-auto" />
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-green-400' : 'text-green-900'}`}>
                {Object.values(notificationTypes).filter(n => n.enabled).length}
              </p>
              <p className={`text-xs ${isDarkMode ? 'text-green-500' : 'text-green-700'}`}>Enabled</p>
            </CardContent>
          </Card>
          <Card className={`${isDarkMode ? 'bg-red-900/10 border-red-900/20' : 'bg-gradient-to-br from-red-50 to-red-100 border-red-200'} border shadow-sm`}>
            <CardContent className="p-3 text-center">
              <AlertTriangle className="h-6 w-6 text-red-600 mb-1 mx-auto" />
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-red-400' : 'text-red-900'}`}>
                {Object.values(notificationTypes).filter(n => n.enabled && n.sound).length}
              </p>
              <p className={`text-xs ${isDarkMode ? 'text-red-500' : 'text-red-700'}`}>With Sound</p>
            </CardContent>
          </Card>
          <Card className={`${isDarkMode ? 'bg-blue-900/10 border-blue-900/20' : 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200'} border shadow-sm`}>
            <CardContent className="p-3 text-center">
              <Mail className="h-6 w-6 text-blue-600 mb-1 mx-auto" />
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-900'}`}>
                {Object.values(notificationTypes).filter(n => n.email).length}
              </p>
              <p className={`text-xs ${isDarkMode ? 'text-blue-500' : 'text-blue-700'}`}>Email Alerts</p>
            </CardContent>
          </Card>
        </div>

        {/* Notification Types */}
        <div className="space-y-4">
          <h3 className={`text-sm font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Alert Categories
          </h3>

          <div className="grid gap-3">
            {notificationCategories.map((category, index) => {
              const Icon = category.icon;
              const settings = notificationTypes[category.id];

              return (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`
                    border rounded-xl p-4 transition-all
                    ${isDarkMode
                      ? `bg-slate-900 border-slate-800`
                      : `${category.bg} ${settings.enabled ? 'border-purple-100' : 'border-gray-100'}`
                    }
                    ${!masterEnabled ? 'opacity-50 pointer-events-none' : ''}
                  `}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start space-x-3">
                      <div className={`
                         w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                         ${isDarkMode ? 'bg-slate-800' : 'bg-white shadow-sm'}
                      `}>
                        <Icon className={`h-5 w-5 ${category.color}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center flex-wrap gap-2">
                          <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {category.title}
                          </p>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${category.priorityColor}`}>
                            {category.priority}
                          </span>
                        </div>
                        <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {category.description}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleToggleNotificationType(category.id, 'enabled')}
                      disabled={!masterEnabled}
                      className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${settings.enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-700'}`}
                    >
                      <div className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${settings.enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                    </button>
                  </div>

                  {/* Delivery Methods */}
                  {settings.enabled && masterEnabled && (
                    <div className={`grid grid-cols-4 gap-2 mt-3 pt-3 border-t ${isDarkMode ? 'border-slate-800' : 'border-gray-200/50'}`}>
                      {[
                        { key: 'sound', icon: Volume2, label: 'Sound', activeColor: 'bg-blue-50 border-blue-200 text-blue-600', darkActiveColor: 'bg-blue-900/30 border-blue-800 text-blue-400' },
                        { key: 'vibrate', icon: Vibrate, label: 'Vibrate', activeColor: 'bg-purple-50 border-purple-200 text-purple-600', darkActiveColor: 'bg-purple-900/30 border-purple-800 text-purple-400' },
                        { key: 'email', icon: Mail, label: 'Email', activeColor: 'bg-green-50 border-green-200 text-green-600', darkActiveColor: 'bg-green-900/30 border-green-800 text-green-400' },
                        { key: 'sms', icon: Smartphone, label: 'SMS', activeColor: 'bg-orange-50 border-orange-200 text-orange-600', darkActiveColor: 'bg-orange-900/30 border-orange-800 text-orange-400' },
                      ].map((method) => {
                        const isActive = settings[method.key];
                        return (
                          <button
                            key={method.key}
                            onClick={() => handleToggleNotificationType(category.id, method.key as any)}
                            className={`
                                  flex flex-col items-center justify-center p-2 rounded-lg border transition-all
                                  ${isActive
                                ? (isDarkMode ? `${method.darkActiveColor}` : `${method.activeColor}`)
                                : (isDarkMode ? 'bg-slate-800 border-slate-700 text-gray-500' : 'bg-white border-gray-100 text-gray-400')
                              }
                                `}
                          >
                            <method.icon className="h-4 w-4 mb-1" />
                            <span className="text-[10px]">{method.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Quiet Hours */}
        <Card className={`border-2 overflow-hidden ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-100'}`}>
          <CardHeader className="pb-3 border-b border-gray-100 dark:border-slate-800">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className={`text-base flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  <Clock className={`h-5 w-5 mr-2 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                  Quiet Hours
                </CardTitle>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Pause notifications during specific hours
                </p>
              </div>
              <button
                onClick={() => setQuietHours(prev => ({ ...prev, enabled: !prev.enabled }))}
                className={`relative w-12 h-7 rounded-full transition-colors ${quietHours.enabled ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-700'}`}
              >
                <div className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${quietHours.enabled ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>
          </CardHeader>
          <AnimatePresence>
            {quietHours.enabled && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <CardContent className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={`text-xs font-medium mb-1 block ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Start Time
                      </label>
                      <input
                        type="time"
                        value={quietHours.startTime}
                        onChange={(e) => setQuietHours(prev => ({ ...prev, startTime: e.target.value }))}
                        className={`w-full h-10 px-3 rounded-lg border ${isDarkMode
                          ? 'bg-slate-800 border-slate-700 text-white'
                          : 'bg-white border-gray-300'
                          }`}
                      />
                    </div>
                    <div>
                      <label className={`text-xs font-medium mb-1 block ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        End Time
                      </label>
                      <input
                        type="time"
                        value={quietHours.endTime}
                        onChange={(e) => setQuietHours(prev => ({ ...prev, endTime: e.target.value }))}
                        className={`w-full h-10 px-3 rounded-lg border ${isDarkMode
                          ? 'bg-slate-800 border-slate-700 text-white'
                          : 'bg-white border-gray-300'
                          }`}
                      />
                    </div>
                  </div>

                  <div className={`flex items-center justify-between rounded-lg p-3 ${isDarkMode ? 'bg-slate-800' : 'bg-white/50 border border-white'}`}>
                    <div>
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Allow Critical Alerts</p>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Emergency notifications bypass quiet hours
                      </p>
                    </div>
                    <button
                      onClick={() => setQuietHours(prev => ({ ...prev, allowCritical: !prev.allowCritical }))}
                      className={`relative w-12 h-7 rounded-full transition-colors ${quietHours.allowCritical ? 'bg-red-600' : 'bg-gray-300 dark:bg-gray-600'}`}
                    >
                      <div className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${quietHours.allowCritical ? 'translate-x-5' : 'translate-x-0'}`} />
                    </button>
                  </div>
                </CardContent>
              </motion.div>
            )}
          </AnimatePresence>
        </Card>

        {/* Test Notification */}
        <div className="pt-2">
          <Button
            onClick={() => {
              toast.success('Test notification', {
                description: 'Your notification settings are working!',
              });
            }}
            variant="outline"
            className={`w-full h-12 dashed border-2 ${isDarkMode ? 'border-slate-700 hover:bg-slate-800' : ''}`}
          >
            <Bell className="h-4 w-4 mr-2" />
            Send Test Notification
          </Button>
        </div>
      </div>

      {/* Footer Actions */}
      <div className={`border-t p-4 space-y-3 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'
        }`}>
        <Button
          onClick={handleSave}
          className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white font-medium text-lg shadow-lg shadow-purple-200 dark:shadow-none"
        >
          <CheckCircle className="h-5 w-5 mr-2" />
          Save Settings
        </Button>
        <Button
          onClick={onClose}
          variant="outline"
          className={`w-full h-12 ${isDarkMode ? 'border-slate-700 text-white hover:bg-slate-800' : ''}`}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
